import { useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { LockKeyhole, ShieldAlert, Eye, EyeOff, CheckCircle2 } from "lucide-react";

/**
 * Hard-gates the entire app when the logged-in user is still on the default
 * password. Shown as a non-dismissable Dialog rendered inside Layout. Until
 * the user successfully changes their password, all underlying UI is blurred
 * and inaccessible.
 */
export default function MandatoryPasswordChange() {
  const { user, refresh, logout } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!user?.is_default_password && !user?.password_needs_upgrade) return null;
  const isUpgrade = !user?.is_default_password && user?.password_needs_upgrade;

  // Real-time validation rules (Password Strength Indicator)
  const rules = [
    { key: "len",   label: "At least 8 characters",            ok: next.length >= 8 },
    { key: "case",  label: "Upper & lower case",               ok: /[A-Z]/.test(next) && /[a-z]/.test(next) },
    { key: "num",   label: "At least one number",              ok: /\d/.test(next) },
    { key: "sym",   label: "At least one special character",   ok: /[^A-Za-z0-9]/.test(next) },
    { key: "match", label: "Confirm matches new password",     ok: next.length > 0 && next === confirm },
    { key: "diff",  label: "Must be different from current",   ok: next.length > 0 && next !== cur },
  ];
  const allOk = rules.every((r) => r.ok) && cur.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) {
      toast.error("Password does not meet all requirements");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed — welcome aboard!");
      setCur(""); setNext(""); setConfirm("");
      await refresh();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await logout(); } catch { /* ignore */ }
  };

  return (
    <Dialog open={true} onOpenChange={() => { /* non-dismissable */ }}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-0 [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="mandatory-password-change-dialog"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br overflow-hidden rounded-t-lg from-blue-600 via-indigo-700 to-violet-800 px-6 py-5 text-white">
          <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
          <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-indigo-300/20 rounded-full blur-2xl" />
          <div className="relative flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-white/15 backdrop-blur-sm ring-1 ring-white/25 flex items-center justify-center">
              <ShieldAlert size={22} strokeWidth={2} />
            </div>
            <div>
              <DialogTitle className="heading text-lg font-extrabold tracking-tight">
                {isUpgrade ? "Strengthen your password" : "Change your password"}
              </DialogTitle>
              <DialogDescription className="text-[12px] text-white/85 mt-0.5">
                {isUpgrade
                  ? "Your current password no longer meets our security policy. Pick a stronger one to continue."
                  : "You're using the default password. Set a strong one to continue."}
              </DialogDescription>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="px-6 py-5 space-y-3 bg-white">
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">
              Current Password
            </Label>
            <div className="relative">
              <Input
                type={showCur ? "text" : "password"}
                value={cur}
                onChange={(e) => setCur(e.target.value)}
                placeholder={isUpgrade ? "Your current password" : "Your current default password"}
                autoComplete="current-password"
                className="pr-9"
                data-testid="mpc-current"
                required
              />
              <button
                type="button"
                onClick={() => setShowCur((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showCur ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">
              New Password
            </Label>
            <div className="relative">
              <Input
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder="Choose a strong new password"
                autoComplete="new-password"
                className="pr-9"
                data-testid="mpc-new"
                required
              />
              <button
                type="button"
                onClick={() => setShowNext((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showNext ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">
              Confirm New Password
            </Label>
            <Input
              type={showNext ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-type the new password"
              autoComplete="new-password"
              data-testid="mpc-confirm"
              required
            />
          </div>

          {/* Live strength rules */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="mpc-rules">
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-600 mb-1.5">
              Requirements
            </div>
            <ul className="grid grid-cols-1 gap-1">
              {rules.map((r) => (
                <li
                  key={r.key}
                  className={`flex items-center gap-1.5 text-[11.5px] ${r.ok ? "text-emerald-700" : "text-slate-500"}`}
                  data-testid={`mpc-rule-${r.key}`}
                >
                  <CheckCircle2
                    size={12}
                    strokeWidth={2.4}
                    className={r.ok ? "text-emerald-600" : "text-slate-300"}
                  />
                  {r.label}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-between gap-3 pt-1">
            <button
              type="button"
              onClick={handleLogout}
              className="text-[11px] text-slate-500 hover:text-slate-700 hover:underline"
              data-testid="mpc-logout"
            >
              Log out instead
            </button>
            <Button
              type="submit"
              disabled={!allOk || loading}
              className="bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="mpc-submit"
            >
              <LockKeyhole size={14} className="mr-1.5" />
              {loading ? "Saving…" : "Change Password"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
